import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { inIframe } from '@/shared/lib/browser';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { Price, Subscription } from '@/shared/types/Billing';

const RQ_GET_CUSTOMER_PORTAL_LINK_KEY = ['getCustomerPortalLink'];

type CustomerPortalProps = {
  baseUrl: string;
  navigateTo: 'portal' | 'updateBillingDetails' | 'updatePlan';
  priceId?: Price['id'];
  subscriptionId?: Subscription['id'];
};

const createLink = ({ baseUrl, navigateTo, priceId, subscriptionId }: CustomerPortalProps) => {
  const routes = {
    portal: baseUrl,
    updateBillingDetails: `${baseUrl}/customer/update`,
    updatePlan: `${baseUrl}/subscriptions/${subscriptionId}/preview/${priceId}?quantity=1`,
  };

  return routes[navigateTo];
};

// todo: check if browser doesn't block opening iframe links
const openLink = (link: CustomerPortalProps['baseUrl']) => {
  if (inIframe()) {
    window.open(link, '_blank')?.focus();
    return;
  }

  window.location.href = link;
};

export const useCustomerPortal = () => {
  const [isLoading, setIsLoading] = useState(false);

  const getCustomerPortalLink = useMutation({
    mutationFn: () => API.getCustomerPortal(window.location.href),
    mutationKey: RQ_GET_CUSTOMER_PORTAL_LINK_KEY,
  });

  const openCustomerPortal = async ({ navigateTo, priceId, subscriptionId }: Omit<CustomerPortalProps, 'baseUrl'>) => {
    try {
      setIsLoading(true);
      const { billing_portal_url: baseUrl } = await getCustomerPortalLink.mutateAsync();

      if (!baseUrl) {
        return;
      }

      const linkToPortal = createLink({ baseUrl, navigateTo, priceId, subscriptionId });

      openLink(linkToPortal);
    } catch (e) {
      showErrorAlert(e);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isCustomerPortalLoading: isLoading,
    openCustomerPortal,
  };
};
